import type { CompanyLinkView } from "@portal/schemas";

/** フォルダ(= カテゴリ)とその中のリンク。 */
export interface Folder {
  name: string;
  links: CompanyLinkView[];
}

/** フォルダごとにまとめる(API は並び順で返す。フォルダは最初に現れた順、中は並び順のまま)。 */
export function groupLinks(links: readonly CompanyLinkView[]): Folder[] {
  const groups = new Map<string, CompanyLinkView[]>();
  for (const l of links) {
    const g = groups.get(l.category) ?? [];
    g.push(l);
    groups.set(l.category, g);
  }
  return [...groups].map(([name, links]) => ({ name, links }));
}

/** 全リンクを、フォルダの順・フォルダ内の順に並べた ID(並べ替えの保存に使う)。 */
export const flattenIds = (folders: readonly Folder[]): string[] =>
  folders.flatMap((f) => f.links.map((l) => l.id));

const moveItem = <T>(items: readonly T[], from: number, to: number): T[] => {
  const next = [...items];
  const [moved] = next.splice(from, 1);
  if (moved !== undefined) next.splice(to, 0, moved);
  return next;
};

/** フォルダの並べ替え(from を to の位置へ)。知らないフォルダ名なら何も変えない。 */
export function moveFolder(folders: readonly Folder[], from: string, to: string): Folder[] {
  const a = folders.findIndex((f) => f.name === from);
  const b = folders.findIndex((f) => f.name === to);
  return a < 0 || b < 0 || a === b ? [...folders] : moveItem(folders, a, b);
}

/** 同じフォルダの中での、リンクの並べ替え(active を over の位置へ)。 */
export function moveLinkInFolder(
  folders: readonly Folder[],
  folder: string,
  activeId: string,
  overId: string,
): Folder[] {
  return folders.map((f) => {
    if (f.name !== folder) return f;
    const a = f.links.findIndex((l) => l.id === activeId);
    const b = f.links.findIndex((l) => l.id === overId);
    return a < 0 || b < 0 || a === b ? f : { ...f, links: moveItem(f.links, a, b) };
  });
}

/**
 * リンクを別のフォルダへ移した、全リンクの並び(移したリンクは、そのフォルダの末尾。サーバーも末尾に置く)。
 * 新しいフォルダ(まだ空)へ移すときは、全体の末尾に置く。
 */
export function moveLinkToFolder(
  links: readonly CompanyLinkView[],
  linkId: string,
  folder: string,
): CompanyLinkView[] {
  const moving = links.find((l) => l.id === linkId);
  if (!moving) return [...links];
  const rest = links.filter((l) => l.id !== linkId);
  const moved = { ...moving, category: folder };
  let lastIndex = -1;
  rest.forEach((l, i) => {
    if (l.category === folder) lastIndex = i;
  });
  return lastIndex < 0
    ? [...rest, moved]
    : [...rest.slice(0, lastIndex + 1), moved, ...rest.slice(lastIndex + 1)];
}

/** 検索語に合うか(タイトル・URL・説明・フォルダ名)。 */
export const matchesQuery = (l: CompanyLinkView, query: string): boolean => {
  const q = query.trim().toLowerCase();
  return q === "" || [l.title, l.url, l.note, l.category].some((v) => v.toLowerCase().includes(q));
};

/** ドラッグの ID。フォルダとリンクは種類で見分ける。 */
export const folderDragId = (name: string) => `folder:${name}`;
export const linkDragId = (id: string) => `link:${id}`;
export const dragKind = (id: string): "folder" | "link" =>
  id.startsWith("folder:") ? "folder" : "link";
export const folderOfDragId = (id: string) => id.slice("folder:".length);
export const linkOfDragId = (id: string) => id.slice("link:".length);
